import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import SearchField from './SearchField';

/*
 * SwitcherMenu — the topbar popover shared by the org circle, the
 * context switcher, and the account avatar (same visual family as the
 * old org menu / FilterMenu popovers). Items with `active` get the
 * brand dot; `footer` items render below a divider; `children` render
 * between items and footer (for custom rows like the dark toggle).
 * `searchable` puts a SearchField above the items (breadcrumb pickers
 * with long lists — jobs, recent events).
 */

export interface SwitcherItem {
    key: string;
    label: string;
    sublabel?: string;
    active?: boolean;
    onClick: () => void;
}

interface SwitcherMenuProps {
    trigger: React.ReactNode;
    triggerTitle: string;
    /* renders above the header — identity blocks and other fixed chrome */
    top?: React.ReactNode;
    header?: string;
    items: SwitcherItem[];
    footer?: SwitcherItem[];
    align?: 'left' | 'right';
    width?: number;
    searchable?: boolean;
    children?: React.ReactNode;
}

export default function SwitcherMenu({
    trigger,
    triggerTitle,
    top,
    header,
    items,
    footer,
    align = 'left',
    width = 280,
    searchable,
    children,
}: SwitcherMenuProps) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) setQuery('');
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const row = (item: SwitcherItem) => (
        <button
            key={item.key}
            type="button"
            onClick={() => {
                setOpen(false);
                item.onClick();
            }}
            className={
                'flex w-full cursor-pointer items-center gap-2 rounded-[8px] border-0 bg-transparent px-2.5 py-2 text-left font-[inherit] text-[13px] hover:bg-secondary ' +
                (item.active ? 'font-semibold' : 'text-muted-foreground')
            }
        >
            <span
                className={
                    'h-1.5 w-1.5 flex-none rounded-full ' +
                    (item.active ? 'bg-brand' : 'bg-transparent')
                }
            />
            <span className="min-w-0 flex-1 truncate">
                {item.label}
                {item.sublabel && (
                    <span className="ml-1.5 font-normal text-faint">
                        {item.sublabel}
                    </span>
                )}
            </span>
        </button>
    );

    return (
        <div ref={rootRef} className="relative flex">
            <button
                type="button"
                title={triggerTitle}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
                className="flex cursor-pointer items-center gap-[7px] border-0 bg-transparent p-0 font-[inherit]"
            >
                {trigger}
            </button>
            {open && (
                <div
                    style={{ width }}
                    className={
                        'absolute top-[calc(100%+8px)] z-50 rounded-[13px] border border-border bg-background p-1.5 shadow-lg ' +
                        (align === 'right' ? 'right-0' : 'left-0')
                    }
                >
                    {top}
                    {header && (
                        <div className="ps-label px-2.5 pb-1 pt-1.5">
                            {header}
                        </div>
                    )}
                    {searchable && (
                        <div className="px-1 pb-1.5 pt-0.5">
                            <SearchField
                                autoFocus
                                value={query}
                                onChange={setQuery}
                                placeholder="Search…"
                                className="w-full"
                            />
                        </div>
                    )}
                    <div className="max-h-[320px] overflow-y-auto">
                        {(searchable && query.trim()
                            ? items.filter((item) =>
                                  (item.label + ' ' + (item.sublabel ?? ''))
                                      .toLowerCase()
                                      .includes(query.trim().toLowerCase()),
                              )
                            : items
                        ).map(row)}
                    </div>
                    {children}
                    {footer && footer.length > 0 && (
                        <>
                            <div className="mx-1 my-1 border-t border-line" />
                            {footer.map(row)}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
